import React, {FC} from "react"

import GenericModal from "@irati/ui/src/GenericModal"
import Flex from "@irati/ui/src/Flex"
import {H2, H6, Body} from "@irati/ui/src/Typography"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Button from "@irati/ui/src/Button"
import mainTheme from "@irati/ui/src/global/theme"
import {BulkRewardFailedEmails} from "@irati/types"
import downloadCsv from "@irati/utils/downloadCsv"
import History from "@irati/utils/history"
import {DistributeRewardActions, DistributeRewardActionTypes} from "../../../stores/DistributeReward/actions"
import {StepNames} from "../../../enums"

interface Props extends InjectIntl {
  failedEmails: BulkRewardFailedEmails[],
  totalEmails: number,
  dispatch: (action: DistributeRewardActionTypes) => void,
  resetCSV: () => void
}

const CSVErrorReportModal: FC<Props> = ({failedEmails, totalEmails, dispatch, resetCSV, formatMessage}) => {
  const successEmails = totalEmails - failedEmails.length
  const errorReportListCsvRows = failedEmails.map(({email, status}) => [email, status])

  const onClose = () => {
    dispatch({
      type: DistributeRewardActions.ON_REJECT_EMAIL_ERRORS
    })
  }

  const onUploadNewCsv = () => {
    dispatch({
      type: DistributeRewardActions.ON_REJECT_EMAIL_ERRORS
    })
    resetCSV()
  }

  const onContinue = () => {
    dispatch({
      type: DistributeRewardActions.ON_ACCEPT_EMAIL_ERRORS
    })
    History.push(`/wallet/flexi-assign/${StepNames.Message}`)
  }


  const downloadCsvFile = () => {
    const headers: string = [
      formatMessage({id: "platform.email.label"}),
      formatMessage({id: "platform.status.label"})
    ].join(",")

    // \ufeff needed to specify the csv may contain utf-8 characters
    let csv = `\ufeff${headers}`
    errorReportListCsvRows.forEach((row) => {
      csv += `\n${row}`
    })
    const fileName = formatMessage({id: "wallet.adminReward.bulkRewards.errorModal.downloadReport.csv.title"})
    downloadCsv(csv, fileName)
  }

  if (failedEmails.length <= 0) {
    return null
  }

  return (
    <GenericModal
      hideActions
      show
      onHide={onClose}>
      <H2 marginBottom={2}>
        <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.title" />
      </H2>

      <Flex direction="column" marginTop={4} marginBottom={4} marginLeft={2}>
        <Flex alignItems="center" marginBottom>
          <Icon icon={IconTypes.circleTickFill} marginRight />
          <Body>
            <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.matchedEmployees" values={{count: successEmails}} />
          </Body>
        </Flex>

        <Flex alignItems="center" marginBottom>
          <Icon icon={IconTypes.circleAlertFill} marginRight />
          <Body color={mainTheme.colors.tertiary.r_400}>
            <FormattedMessage
              id="wallet.adminReward.bulkRewards.errorModal.notMatchedEmployees"
              values={{count: failedEmails.length}} />
          </Body>
        </Flex>
      </Flex>

      <H6 marginBottom={4}>
        <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.downloadReport.title" />
      </H6>

      <Button tertiary cta onClick={downloadCsvFile} marginLeft={2}>
        <Icon icon={IconTypes.downloadReport} size={22} />
        <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.downloadReport.cta.title" />
      </Button>

      <Flex gap="1rem" marginTop={5}>
        <Button large full onClick={onContinue}>
          <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.cta.title.continue" />
        </Button>
        <Button primary large full onClick={onUploadNewCsv}>
          <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.cta.title.upload" />
        </Button>
      </Flex>
    </GenericModal>
  )
}

export default injectIntl(CSVErrorReportModal)
